import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Calendar, User, ArrowRight, Tag, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ArticlesPage() {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  
  const page = parseInt(searchParams.get('page') || '1');
  const category = searchParams.get('category') || '';

  useEffect(() => {
    const fetchArticles = async () => {
      setLoading(true);
      try {
        const params = { page, limit: 12 };
        if (category) params.category = category;
        const res = await axios.get(`${API}/api/articles`, { params });
        setArticles(res.data.articles);
        setTotal(res.data.total);
        setPages(res.data.pages);
        setCategories(res.data.categories || []);
      } catch (err) {
        console.error('Failed to load articles:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, [page, category]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-AU', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  const setPage = (p) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', p.toString());
    setSearchParams(params);
  };

  const setCategory_ = (cat) => {
    const params = new URLSearchParams();
    if (cat) params.set('category', cat);
    setSearchParams(params);
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      {/* Header */}
      <header className="bg-[#2E4033] text-white">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="articles-logo-link">
            <img src="/contractwiser-logo.svg" alt="ContractWiser" className="h-8 w-8" />
            <span className="font-semibold text-lg tracking-tight">ContractWiser</span>
          </Link>
          <nav className="flex items-center gap-6">
            <Link to="/upload" className="text-sm text-white/80 hover:text-white transition-colors">Upload Contract</Link>
            <Link to="/articles" className="text-sm text-white font-medium">Articles</Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-[#2E4033] text-white pb-16 pt-8">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4 tracking-tight">Property Insights</h1>
          <p className="text-lg text-white/70 max-w-2xl mx-auto">
            Expert guides on Australian property contracts, buying tips, and legal insights to help you make informed decisions.
          </p>
        </div>
      </section>

      {/* Categories */}
      {categories.length > 0 && (
        <div className="max-w-6xl mx-auto px-4 -mt-5">
          <div className="flex flex-wrap gap-2 justify-center">
            <button
              onClick={() => setCategory_('')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all shadow-sm ${
                !category ? 'bg-[#2E4033] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
              data-testid="category-all"
            >
              All Articles
            </button>
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setCategory_(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all shadow-sm ${
                  category === cat ? 'bg-[#2E4033] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
                data-testid={`category-${cat}`}
              >
                <Tag size={12} className="inline mr-1.5 -mt-0.5" />{cat}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Articles Grid */}
      <main className="max-w-6xl mx-auto px-4 py-12">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1,2,3].map(i => (
              <div key={i} className="bg-white rounded-xl overflow-hidden shadow-sm animate-pulse">
                <div className="h-48 bg-gray-200" />
                <div className="p-6 space-y-3">
                  <div className="h-4 bg-gray-200 rounded w-1/3" />
                  <div className="h-6 bg-gray-200 rounded w-3/4" />
                  <div className="h-4 bg-gray-200 rounded w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-500 text-lg">No articles published yet. Check back soon!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="articles-grid">
            {articles.map(article => (
              <Link
                key={article.slug}
                to={`/articles/${article.slug}`}
                className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all group"
                data-testid={`article-card-${article.slug}`}
              >
                {article.featured_image_url && (
                  <div className="h-48 overflow-hidden">
                    <img
                      src={article.featured_image_url}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                )}
                <div className="p-6">
                  {article.category && (
                    <span className="inline-block px-2.5 py-0.5 bg-[#2E4033]/10 text-[#2E4033] text-xs font-medium rounded-full mb-3">
                      {article.category}
                    </span>
                  )}
                  <h2 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-[#2E4033] transition-colors line-clamp-2">
                    {article.title}
                  </h2>
                  {article.excerpt && (
                    <p className="text-gray-500 text-sm mb-4 line-clamp-3">{article.excerpt}</p>
                  )}
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1">
                        <User size={12} />{article.author || 'ContractWiser'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar size={12} />{formatDate(article.published_at)}
                      </span>
                    </div>
                    <ArrowRight size={14} className="text-[#2E4033] group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex justify-center gap-2 mt-12" data-testid="articles-pagination">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page <= 1}
              className="p-2 rounded-lg border border-gray-200 disabled:opacity-30 hover:bg-gray-50"
            >
              <ChevronLeft size={18} />
            </button>
            {Array.from({ length: pages }, (_, i) => i + 1).map(p => (
              <button
                key={p}
                onClick={() => setPage(p)}
                className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                  p === page ? 'bg-[#2E4033] text-white' : 'border border-gray-200 hover:bg-gray-50'
                }`}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => setPage(Math.min(pages, page + 1))}
              disabled={page >= pages}
              className="p-2 rounded-lg border border-gray-200 disabled:opacity-30 hover:bg-gray-50"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        )}
      </main>

      {/* CTA */}
      <section className="bg-[#2E4033] text-white py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold mb-4">Ready to understand your contract?</h2>
          <p className="text-white/70 mb-6">Upload your property contract and get a plain-English analysis report.</p>
          <Link
            to="/upload"
            className="inline-flex items-center gap-2 bg-white text-[#2E4033] px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors"
            data-testid="articles-cta-upload"
          >
            Upload Contract <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
